import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { Badge } from './ui/Badge';
import { EmptyState } from './ui/EmptyState';
import { Pagination } from './ui/Pagination';
import { SkeletonRow } from './ui/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

function Roles() {
  const [roles, setRoles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [viewingRole, setViewingRole] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [pageError, setPageError] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchRoles();
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }
    const timer = setTimeout(() => setSuccessMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!pageError) {
      return undefined;
    }
    const timer = setTimeout(() => setPageError(''), 5000);
    return () => clearTimeout(timer);
  }, [pageError]);

  useEffect(() => {
    if (!formError) {
      return undefined;
    }
    const timer = setTimeout(() => setFormError(''), 5000);
    return () => clearTimeout(timer);
  }, [formError]);

  const fetchRoles = async () => {
    try {
      setPageError('');
      setListLoading(true);
      const url = `${API_BASE_URL}/api/roles?page=${currentPage}&size=${pageSize}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`;
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to fetch roles');
      }
      const data = await response.json();
      setRoles(Array.isArray(data.content) ? data.content : []);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      setRoles([]);
      setPageError(error.message || 'Unable to load roles');
    } finally {
      setListLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) return;

    setFormError('');
    setLoading(true);
    try {
      const url = editingRole 
        ? `${API_BASE_URL}/api/roles/${editingRole.id}`
        : `${API_BASE_URL}/api/roles`;
      const method = editingRole ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('token')
            ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
            : {}),
        },
        body: JSON.stringify({ name: roleName }),
      });

      if (response.ok) {
        setRoleName('');
        setShowForm(false);
        setEditingRole(null);
        setCurrentPage(0);
        setSuccessMessage(editingRole ? 'Role updated successfully' : 'Role created successfully');
        fetchRoles();
      } else {
        const errData = await response.json().catch(() => ({}));
        setFormError(errData.error || (editingRole ? 'Failed to update role' : 'Failed to create role'));
      }
    } catch (error) {
      setFormError(error.message || 'Error saving role');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role) => {
    setFormError('');
    setEditingRole(role);
    setRoleName(role.name);
    setShowForm(true);
  };

  const handleView = (role) => {
    setViewingRole(role);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRole(null);
    setRoleName('');
    setFormError('');
    setShowDeleteConfirm(false);
  };

  const handleDeleteRole = async () => {
    if (!editingRole) {
      return;
    }

    setFormError('');
    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/roles/${editingRole.id}`, {
        method: 'DELETE',
        headers: localStorage.getItem('token')
          ? { Authorization: `Bearer ${localStorage.getItem('token')}` }
          : {},
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to delete role');
      }

      setSuccessMessage('Role archived successfully');
      setShowDeleteConfirm(false);
      handleCancel();
      setCurrentPage(0);
      fetchRoles();
    } catch (error) {
      setFormError(error.message || 'Error deleting role');
    } finally {
      setDeleting(false);
    }
  };

  const searchIcon = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title="Roles Management"
            actions={
              <>
                <div className="w-full sm:w-64">
                  <Input
                    type="text"
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(0);
                    }}
                    leftIcon={searchIcon}
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    setEditingRole(null);
                    setRoleName('');
                    setShowForm(true);
                  }}
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  }
                >
                  Add Role
                </Button>
              </>
            }
          />
        </CardHeader>
        <CardBody>
          <AlertMessage
            message={successMessage}
            type="success"
            onClose={() => setSuccessMessage('')}
            className="mb-4"
          />
          <AlertMessage
            message={pageError}
            type="error"
            onClose={() => setPageError('')}
            className="mb-4"
          />

          {!listLoading && roles.length === 0 ? (
            <EmptyState
              title={searchTerm ? 'No roles match your search' : 'No roles found'}
              description={searchTerm
                ? 'Try a different search term or clear the filter.'
                : 'Get started by creating your first role.'}
              icon={
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              }
              action={
                !searchTerm ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      setEditingRole(null);
                      setRoleName('');
                      setShowForm(true);
                    }}
                  >
                    Add your first role
                  </Button>
                ) : null
              }
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Role Name</th>
                    <th>Created On</th>
                    <th className="text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading
                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} columns={3} />)
                    : roles.map((role) => (
                        <tr key={role.id}>
                          <td>
                            <Badge tone="primary" size="md">{role.name}</Badge>
                          </td>
                          <td className="text-slate-500">
                            {role.createdOn
                              ? new Date(role.createdOn).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                              : '—'}
                          </td>
                          <td className="pr-6">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleView(role)}
                                className="icon-action icon-action-primary"
                                title="View"
                                aria-label={`View ${role.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEdit(role)}
                                className="icon-action icon-action-primary"
                                title="Edit"
                                aria-label={`Edit ${role.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={(p) => setCurrentPage(p)}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setCurrentPage(0);
            }}
          />
        </CardBody>
      </Card>

      <Modal
        open={showForm}
        onClose={handleCancel}
        title={editingRole ? 'Edit Role' : 'Add New Role'}
        description={editingRole ? 'Update the name for this role.' : 'Create a new role to start assigning permissions.'}
        size="sm"
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        }
        footer={
          <div className="flex items-center justify-between w-full gap-3">
            <div>
              {editingRole && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-rose-600 hover:bg-rose-50"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loading || deleting}
                  loading={deleting}
                >
                  Delete role
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={loading || deleting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="role-form"
                variant="primary"
                loading={loading}
                disabled={loading || deleting}
              >
                {editingRole ? 'Update role' : 'Save role'}
              </Button>
            </div>
          </div>
        }
      >
        <form id="role-form" onSubmit={handleSubmit} className="py-6 space-y-4">
          <AlertMessage
            message={formError}
            type="error"
            onClose={() => setFormError('')}
          />
          <Input
            label="Role Name"
            placeholder="e.g., Warehouse Manager"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            required
            autoFocus
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={showDeleteConfirm && Boolean(editingRole)}
        title="Delete Role"
        message={editingRole ? `Do you want to archive role "${editingRole.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteRole}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={deleting}
        variant="danger"
      />

      <Modal
        open={Boolean(viewingRole)}
        onClose={() => setViewingRole(null)}
        title="Role Details"
        size="sm"
        headerVariant="plain"
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        }
        footer={
          <Button type="button" variant="secondary" onClick={() => setViewingRole(null)}>
            Close
          </Button>
        }
      >
        {viewingRole && (
          <div className="py-6 space-y-5">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Role Name</label>
              <div className="mt-2">
                <Badge tone="primary" size="md">{viewingRole.name}</Badge>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Created On</label>
              <p className="mt-1.5 text-sm text-slate-700">
                {viewingRole.createdOn
                  ? new Date(viewingRole.createdOn).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    })
                  : '—'}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Roles;
